// ═══════════════════════════════════════════════════════════════
// SEED: Catálogo inicial de Tipos de Tarea
// ═══════════════════════════════════════════════════════════════
//
// Ejecutar desde la consola del navegador (logueado como consultor):
//   await __seedTiposTarea()
//
// Idempotente: si ya existe un tipo con el mismo código, lo saltea.
// ═══════════════════════════════════════════════════════════════

import { getTipoTareaPorCodigo, upsertTipoTarea } from '@core/lib/tiposTarea'

const CATALOGO_INICIAL = [
  // ─── Publicidad (generados por dashboards) ───
  {
    codigo: 'pausar_publicacion',
    nombre: 'Pausar publicación',
    descripcion: 'Pausar una publicación de MercadoLibre que no cumple criterios de rentabilidad o tiene problemas.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 60,
    ventanaAlertaMinutos: 15,
    prioridadDefault: 'alta',
    orden: 10,
  },
  {
    codigo: 'activar_publicacion',
    nombre: 'Activar publicación',
    descripcion: 'Reactivar una publicación pausada que ahora cumple los criterios.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 120,
    ventanaAlertaMinutos: 30,
    prioridadDefault: 'normal',
    orden: 20,
  },
  {
    codigo: 'ajustar_precio',
    nombre: 'Ajustar precio',
    descripcion: 'Modificar el precio de una publicación según análisis de competencia o rentabilidad.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 240,
    ventanaAlertaMinutos: 60,
    prioridadDefault: 'normal',
    orden: 30,
  },
  {
    codigo: 'revisar_campana',
    nombre: 'Revisar campaña',
    descripcion: 'Revisar el rendimiento de una campaña de Product Ads y tomar acciones correctivas.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 480,
    ventanaAlertaMinutos: 60,
    prioridadDefault: 'normal',
    orden: 40,
  },
  {
    codigo: 'optimizar_publicacion',
    nombre: 'Optimizar publicación',
    descripcion: 'Mejorar título, fotos, descripción o atributos de una publicación para aumentar conversión.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 1440,
    ventanaAlertaMinutos: 120,
    prioridadDefault: 'normal',
    orden: 50,
  },
  {
    codigo: 'escalar_problema_publicacion',
    nombre: 'Escalar problema de publicación',
    descripcion: 'Escalar un problema con una publicación que requiere intervención de un supervisor.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 30,
    ventanaAlertaMinutos: 10,
    prioridadDefault: 'urgente',
    orden: 60,
  },
  {
    codigo: 'revisar_publicacion',
    nombre: 'Revisar publicación',
    descripcion: 'Revisar una publicación marcada por el sistema o por un dashboard para validar su estado.',
    moduloOrigen: 'publicidad',
    dashboardOrigen: 'publicidad',
    slaDefaultMinutos: 240,
    ventanaAlertaMinutos: 60,
    prioridadDefault: 'normal',
    orden: 70,
  },

  // ─── Tareas manuales / genéricas ───
  {
    codigo: 'tarea_manual',
    nombre: 'Tarea manual',
    descripcion: 'Tarea creada manualmente sin tipo específico.',
    moduloOrigen: null,
    dashboardOrigen: null,
    slaDefaultMinutos: null, // Sin SLA por defecto
    ventanaAlertaMinutos: 15,
    prioridadDefault: 'normal',
    orden: 100,
  },
  {
    codigo: 'responder_consulta',
    nombre: 'Responder consulta',
    descripcion: 'Responder una consulta o pregunta de un cliente o compañero de equipo.',
    moduloOrigen: 'mensajeria',
    dashboardOrigen: null,
    slaDefaultMinutos: 60,
    ventanaAlertaMinutos: 15,
    prioridadDefault: 'alta',
    orden: 110,
  },
  {
    codigo: 'seguimiento_cliente',
    nombre: 'Seguimiento de cliente',
    descripcion: 'Realizar seguimiento con un cliente sobre un tema pendiente.',
    moduloOrigen: 'gestion',
    dashboardOrigen: null,
    slaDefaultMinutos: 1440,
    ventanaAlertaMinutos: 120,
    prioridadDefault: 'normal',
    orden: 120,
  },
  {
    codigo: 'revision_periodica',
    nombre: 'Revisión periódica',
    descripcion: 'Revisión periódica de métricas, estado de cuenta o indicadores.',
    moduloOrigen: 'diagnostico',
    dashboardOrigen: null,
    slaDefaultMinutos: 2880, // 48hs
    ventanaAlertaMinutos: 240,
    prioridadDefault: 'baja',
    orden: 130,
  },
  {
    codigo: 'capacitacion',
    nombre: 'Capacitación',
    descripcion: 'Realizar una capacitación o sesión de entrenamiento con el equipo del cliente.',
    moduloOrigen: 'gestion',
    dashboardOrigen: null,
    slaDefaultMinutos: null, // Se agenda, no tiene SLA de respuesta
    ventanaAlertaMinutos: 60,
    prioridadDefault: 'normal',
    orden: 140,
  },
]

async function seedTiposTarea() {
  console.log('🌱 Seeding catálogo de tipos de tarea...')
  let creados = 0
  let existentes = 0
  let errores = 0

  for (const tipo of CATALOGO_INICIAL) {
    try {
      const existente = await getTipoTareaPorCodigo(tipo.codigo)
      if (existente) {
        console.log(`  ⏭ ${tipo.codigo} ya existe (${existente.id})`)
        existentes++
        continue
      }
      const resultado = await upsertTipoTarea(null, tipo)
      console.log(`  ✅ ${tipo.codigo} → ${resultado.id}`)
      creados++
    } catch (e) {
      console.error(`  ❌ ${tipo.codigo}:`, e.message)
      errores++
    }
  }

  console.log(`\n🌱 Seed completado: ${creados} creados, ${existentes} ya existían, ${errores} errores`)
  return { creados, existentes, errores }
}

// Exponer en window
if (typeof window !== 'undefined') {
  window.__seedTiposTarea = seedTiposTarea
}
